import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../hooks/AppContext';
import { prop } from 'ramda';
import usePrice from 'src/apps/envizi-price-estimator/hooks/usePrice';
import { swapCountryAndLanguage } from 'src/apps/envizi-price-estimator/utility/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import {
  Button,
  ButtonSet,
  Checkbox,
  SkeletonPlaceholder,
  Tooltip,
} from 'carbon-components-react';
import messages from 'src/apps/envizi-price-estimator/locales/messages';
import { isEmpty } from 'ramda';
import { useSetCookie } from 'src/apps/envizi-price-estimator/hooks/useSetCookie';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import {
  EST_ACCT_LIMIT,
  PREM_ACCT_LIMIT,
  SCOPE_3_POSITION,
  STD_ACCT_LIMIT,
} from '../../../constant/spread';
import { useLang } from 'src/common/hooks/widgets/useLang';
import countryList from '../../../services/currencies.json';
import {
  convertToString,
  createUseTranslation,
} from '../../../../../common/hooks/widgets/useTranslation';
import { useEnviziSelector } from '../../../lib/redux/hooks';
import { ArrowRight20, Restart16 } from '@carbon/icons-react';
import { shortcode } from 'src/apps/envizi-price-estimator/EnviziPriceEstimator.widget';

type PropsFunc = {
  isOpen: boolean;
  langCode: string;
  answers: answerProps[];
  insight?: boolean;
  tileValue: string;
  countryToTest: string;
  test: boolean;
  totalCostMonthly?: string;
  requestQuote: (...args: any[]) => void;
  totalValue: number;
  showPrice?: boolean;
  checkCalcBtnState?: boolean;
  handlePrice?: () => void;
  formId: string;
};

type answerProps = {
  state: string;
  answer: string;
};

const Result = ({
  isOpen,
  langCode,
  answers,
  tileValue,
  countryToTest,
  test,
  requestQuote,
  totalValue,
  showPrice,
  checkCalcBtnState,
  handlePrice,
  formId,
}: PropsFunc) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const { appState, appAction } = useAppContext();
  const appPoints = prop('appPoints')(appState);
  const cookieURL = useCookiesURL(shortcode);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [monthly, setMonthly] = useState(false);
  const [] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const AEMMessages = useEnviziSelector((state) => state.translations.messages);
  const useTranslation = createUseTranslation(messages, AEMMessages);
  const [spinner, setSpinner] = useState(true);
  const [lowerPrice, higherPrice] = usePrice({
    appPoints,
    localeCode,
    currency,
    tileValue,
    country,
    monthly,
  });
  const results: string[] = [];
  !isEmpty(answers) &&
    answers.map((item: answerProps) =>
      results.push(`${item.state} : ${item.answer.split('Cookie-')[1]}`),
    );
  const price = { lowerPrice, higherPrice };

  useEffect(() => {
    if (lowerPrice && higherPrice) {
      appAction({
        type: 'price',
        value: price,
      });
    }
  }, [lowerPrice, higherPrice]);

  const scope3Option = prop('scope3')(appState);
  const scope3Selection = `Scope 3 emissions : ${scope3Option}`;

  const getFinalResult = () => {
    // append the scoping 3 emission answer at index 4 in order to send answer to cookie in proper sequence
    for (let i = results.length; i > SCOPE_3_POSITION; i--) {
      results[i] = results[i - 1];
    }
    results[SCOPE_3_POSITION] = scope3Selection;
    return results;
  };

  const finalResults = scope3Option ? getFinalResult() : results;
  useEffect(() => {
    if (appPoints && answers.length) {
      useSetCookie(
        appPoints,
        finalResults,
        cookieURL,
        lowerPrice as string,
        higherPrice as string,
        monthly,
        formId ? formId : '',
      );
    }
  }, [appPoints, answers, lowerPrice, higherPrice]);

  useEffect(() => {
    if (showPrice) {
      setTimeout(() => setSpinner(false), 1000);
    }
  }, [showPrice, spinner, monthly, formId]);

  const setCookie = () => {
    useSetCookie(
      appPoints,
      finalResults,
      cookieURL,
      lowerPrice as string,
      higherPrice as string,
      monthly,
      formId ? formId : '',
    );
  };

  const pkg =
    totalValue <= EST_ACCT_LIMIT
      ? convertToString(useTranslation('est_pkg_label', true))
      : totalValue > EST_ACCT_LIMIT && totalValue <= STD_ACCT_LIMIT
      ? convertToString(useTranslation('std_pkg_label', true))
      : convertToString(useTranslation('prem_pkg_label', true));

  const pkgInfo =
    totalValue <= EST_ACCT_LIMIT
      ? convertToString(useTranslation('est_pkg_info', true))
      : totalValue > EST_ACCT_LIMIT && totalValue <= STD_ACCT_LIMIT
      ? convertToString(useTranslation('std_pkg_info', true))
      : convertToString(useTranslation('prem_pkg_info', true));

  return isOpen ? (
    <>
      <div className="recommend">
        {totalValue <= PREM_ACCT_LIMIT || isNaN(totalValue) ? (
          <>
            <h6>{useTranslation('recomm_label')}:</h6>
            <div className="page_sub_heading">{pkg}</div>
            <div className="price_table_comp__requirement modal-mt">
              {pkgInfo}
            </div>
            <hr />
            {showPrice && (
              <Checkbox
                checked={monthly}
                labelText={useTranslation('monthly')}
                id={'monthly'}
                onChange={() => {
                  setMonthly(!monthly);
                  setSpinner(!spinner);
                }}
              />
            )}
            {showPrice && (
              <div>
                <br />
                {monthly ? (
                  <div className="price_table_comp__bodycopy marTop">
                    {useTranslation('estmt_price_monthly')} *
                  </div>
                ) : (
                  <div className="price_table_comp__bodycopy marTop">
                    {useTranslation('estmt_price_yearly')} *
                  </div>
                )}

                <div>
                  <span className="pricing-table-item--header-amount">
                    {spinner ? (
                      <SkeletonPlaceholder className="envizi_widget__sticky-nav-skeleton" />
                    ) : (
                      lowerPrice
                    )}
                  </span>
                  <div> - {useTranslation('to')} - </div>
                  <span className="pricing-table-item--header-amount">
                    {spinner ? (
                      <SkeletonPlaceholder className="envizi_widget__sticky-nav-skeleton" />
                    ) : (
                      higherPrice
                    )}
                  </span>
                </div>
                <div className="price_table_comp__disclaimer">
                  * {useTranslation('price_tax_text')}
                </div>
              </div>
            )}

            {showPrice ? (
              <ButtonSet className="envizi_widget__stack-button" stacked>
                <Button
                  id="request_btn"
                  onClick={() => requestQuote(setCookie, 'Buy now')}
                  renderIcon={ArrowRight20}
                >
                  {useTranslation('buy_btn_label')}
                </Button>
                <div className="reset-item" onClick={handlePrice}>
                  <span>{useTranslation('start_over')}</span>
                  <Restart16 className="restart-icon" />
                </div>
              </ButtonSet>
            ) : (
              <>
                <div className="page_body ibm-mb-1">
                  <span>{useTranslation('step_2_message')}</span>
                </div>
                <br />
                <Button
                  id="calculate"
                  onClick={handlePrice}
                  renderIcon={ArrowRight20}
                  disabled={checkCalcBtnState}
                >
                  {useTranslation('calc_btn_price_label')}
                </Button>
              </>
            )}
          </>
        ) : (
          <div id="footnote">
            <div className="bx--row mar6 mar2">
              <div className="bx--col-lg-16">
                <div className="page_sub_heading">
                  {useTranslation('custom_quote_msg')}
                </div>
              </div>
            </div>
            <Button
              id="request_btn"
              onClick={() => requestQuote(setCookie, 'Contact us for a quote')}
              renderIcon={ArrowRight20}
            >
              {useTranslation('contact_btn')}
            </Button>
          </div>
        )}
      </div>
    </>
  ) : null;
};

export default Result;
